import type {
  PrinterProfile,
  SymbolDataStructure,
  Warning,
} from '@tsagko/label-contracts'
import { isFixedLengthAi } from '@tsagko/label-contracts'
import { escapeZplText } from '../escape-zpl-text'
import { resolveValueSource } from '../internal/units'

/**
 * GS1-128 helpers (sub-task 2a.10.9).
 *
 * GS1-128 (formerly UCC/EAN-128) is Code-128 with FNC1 separators used to
 * encode GS1 Application Identifiers (AIs). We emit the `>8` FNC1 form: a
 * leading `>8` (FNC1), then `<ai><value>` concatenated, with a `>8` FNC1
 * separator inserted after each variable-length AI that is NOT the last
 * element. Fixed-length (GS1 predefined-length) AIs need no separator. The
 * renderer/printer inserts the FNC1 codeword; the PAYLOAD never carries
 * literal parentheses. The parenthesized `(AI)data(AI)data…` human-readable
 * form is returned separately as `hri` — the pipeline renders it as its own
 * sized ^A0 line (the native ^BC interpretation line is disabled).
 *
 * Payload derivation rules:
 *
 *   - `gs1-ai` (primary): for each element `{ ai, value }`, resolve the
 *                         ValueSource, escape through `escapeZplText` (so
 *                         unsafe `^`/`~` chars in any AI value are sanitised
 *                         per-element), and concatenate as `<ai><value>`,
 *                         with a `>8` FNC1 separator after each
 *                         variable-length AI that is not last. The whole
 *                         payload is prefixed with `>8`.
 *                         Empty elements (after resolution + escape) are
 *                         silently skipped. An all-empty list returns
 *                         `undefined` so the caller in `compileSymbol` can
 *                         silent-skip the primitive.
 *   - `raw`    → resolve the ValueSource verbatim (permissive; lint warns).
 *   - `url`    → resolve the ValueSource verbatim (permissive; lint warns).
 *   - `gtin`   → resolve the ValueSource verbatim (permissive; lint warns).
 *
 * For `raw`/`url`/`gtin`, returns `undefined` when the resolved payload is
 * empty.
 *
 * NOTE: This helper is unique among the symbology arms in that it escapes
 * per-element *inside* the helper. The other arms (`code-128`, `qr`,
 * `ean-13`) escape the whole payload string in `compileSymbol`. For
 * `gs1-128` we have to escape per-element so the AI digits (and the `>8`
 * FNC1 prefix/separators) are never themselves transformed by the escape
 * table — that's why the escape call lives here. The caller in
 * `compileSymbol` should NOT re-escape the `gs1-ai` payload.
 *
 * Warnings from `escapeZplText` flow back through the returned shape's
 * `warnings` field (sub-task 2a.10.15 refactor). The prior signature took a
 * mutable `warnings: Warning[]` out-parameter; the return-shape form is
 * easier to reason about and aligns with `compileSymbol`'s post-refactor
 * signature. Callers that don't care about warnings can ignore the field.
 */
export function derivePayloadForGs1128(
  structure: SymbolDataStructure,
  data: Record<string, unknown>,
  encoding: PrinterProfile['encoding'] = 'UTF8'
): { payload: string | undefined; hri: string | undefined; warnings: Warning[] } {
  const warnings: Warning[] = []
  let resolved: string
  let hri: string | undefined
  switch (structure.kind) {
    case 'gs1-ai': {
      // Build the verified >8 FNC1 form: leading FNC1, then <ai><value> with an
      // FNC1 separator after each VARIABLE-length AI that is followed by another
      // element. Fixed-length (GS1 predefined-length) AIs need no separator.
      // Parentheses are NOT emitted — they only appear in the returned `hri`
      // (the human-readable form, matching the GS1 `(AI)data(AI)data…`
      // convention the native ^BC interpretation line used).
      const parts: { ai: string; value: string }[] = []
      for (const el of structure.elements) {
        const value = resolveValueSource(el.value, data)
        const escapedResult = escapeZplText(value, encoding)
        if (escapedResult.warnings.length > 0) warnings.push(...escapedResult.warnings)
        const escaped = escapedResult.value
        if (escaped.length === 0) continue // silent-skip empty element
        parts.push({ ai: el.ai, value: escaped })
      }
      if (parts.length === 0) {
        return { payload: undefined, hri: undefined, warnings }
      }
      let out = '>8'
      parts.forEach((p, i) => {
        out += p.ai + p.value
        const isLast = i === parts.length - 1
        if (!isFixedLengthAi(p.ai) && !isLast) out += '>8'
      })
      resolved = out
      hri = parts.map(p => `(${p.ai})${p.value}`).join('')
      break
    }
    case 'raw':
    case 'url':
    case 'gtin':
      resolved = resolveValueSource(structure.value, data)
      // hri stays undefined: these kinds get escaped by the CALLER (see the
      // per-element escape note above), so the caller derives the displayed
      // form from the escaped payload (stripping any hand-typed `>8`).
      break
    default: {
      const _exhaustive: never = structure
      throw new Error(
        `derivePayloadForGs1128: unknown structure ${JSON.stringify(_exhaustive)}`
      )
    }
  }
  if (resolved.length === 0) {
    return { payload: undefined, hri: undefined, warnings }
  }
  return { payload: resolved, hri, warnings }
}
